import React, { useEffect, useState, useCallback } from 'react';
import { Table, Select, DatePicker, Card, Typography, Row, Col, Tag, Space, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../api/client';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const ACTION_LABELS: Record<string, { label: string; color: string }> = {
  login: { label: 'Вход', color: 'blue' },
  logout: { label: 'Выход', color: 'default' },
  create: { label: 'Создание', color: 'green' },
  update: { label: 'Изменение', color: 'orange' },
  delete: { label: 'Удаление', color: 'red' },
  view: { label: 'Просмотр', color: 'cyan' },
  export: { label: 'Экспорт', color: 'purple' },
};

const ENTITY_LABELS: Record<string, string> = {
  employee: 'Сотрудник',
  user: 'Пользователь',
  department: 'Подразделение',
  position: 'Должность',
  system: 'Система',
};

const AuditLog: React.FC = () => {
  const [logs, setLogs] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [loading, setLoading] = useState(false);
  const [actionFilter, setActionFilter] = useState<string | undefined>();
  const [entityFilter, setEntityFilter] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [userFilter, setUserFilter] = useState<number | undefined>();

  useEffect(() => {
    api.get('/users').then((res) => setUsers(res.data.items)).catch(() => {});
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, per_page: perPage };
      if (actionFilter) params.action = actionFilter;
      if (entityFilter) params.entity_type = entityFilter;
      if (userFilter) params.user_id = userFilter;
      if (dateRange) {
        params.date_from = dateRange[0].format('YYYY-MM-DD');
        params.date_to = dateRange[1].format('YYYY-MM-DD');
      }

      const res = await api.get('/audit', { params });
      setLogs(res.data.items);
      setTotal(res.data.total);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [page, perPage, actionFilter, entityFilter, userFilter, dateRange]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const columns = [
    {
      title: 'Дата',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (v: string) => v ? dayjs(v).format('DD.MM.YYYY HH:mm:ss') : '—',
    },
    {
      title: 'Пользователь',
      key: 'user',
      width: 160,
      render: (_: any, rec: any) => rec.display_name || rec.username || '—',
    },
    {
      title: 'Действие',
      dataIndex: 'action',
      key: 'action',
      width: 120,
      render: (v: string) => {
        const a = ACTION_LABELS[v] || { label: v, color: 'default' };
        return <Tag color={a.color}>{a.label}</Tag>;
      },
    },
    {
      title: 'Объект',
      key: 'entity',
      width: 150,
      render: (_: any, rec: any) => {
        const label = ENTITY_LABELS[rec.entity_type] || rec.entity_type || '—';
        return rec.entity_id ? `${label} #${rec.entity_id}` : label;
      },
    },
    {
      title: 'Изменения',
      dataIndex: 'changes',
      key: 'changes',
      render: (v: any) => {
        if (!v || Object.keys(v).length === 0) return '—';
        return (
          <pre style={{ margin: 0, fontSize: 12, maxWidth: 400, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
            {JSON.stringify(v, null, 2)}
          </pre>
        );
      },
    },
    {
      title: 'IP',
      dataIndex: 'ip_address',
      key: 'ip_address',
      width: 130,
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4} style={{ margin: 0 }}>Журнал действий</Title>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={fetchLogs}>Обновить</Button>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Пользователь"
              allowClear
              style={{ width: '100%' }}
              value={userFilter}
              onChange={(v) => { setUserFilter(v); setPage(1); }}
            >
              {users.map((u) => (
                <Option key={u.id} value={u.id}>{u.display_name || u.username}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Действие"
              allowClear
              style={{ width: '100%' }}
              value={actionFilter}
              onChange={(v) => { setActionFilter(v); setPage(1); }}
            >
              {Object.entries(ACTION_LABELS).map(([key, val]) => (
                <Option key={key} value={key}>{val.label}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Тип объекта"
              allowClear
              style={{ width: '100%' }}
              value={entityFilter}
              onChange={(v) => { setEntityFilter(v); setPage(1); }}
            >
              {Object.entries(ENTITY_LABELS).map(([key, val]) => (
                <Option key={key} value={key}>{val}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              format="DD.MM.YYYY"
              style={{ width: '100%' }}
              onChange={(dates) => {
                setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null);
                setPage(1);
              }}
            />
          </Col>
        </Row>
      </Card>

      <Table
        dataSource={logs}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{
          current: page,
          pageSize: perPage,
          total,
          onChange: (p) => setPage(p),
          showTotal: (t) => `Всего: ${t}`,
        }}
        size="small"
        scroll={{ x: 900 }}
      />
    </div>
  );
};

export default AuditLog;
